import React, {useEffect, useState} from 'react';
import {getServiceCode} from "../../../api/serviceCode/ServiceCodeService";
import {CacheType, CodeIdType, CodeType, ContentCategory, UserIdType, UserType} from "../../../constant/CodeSpecNum";
import {useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import Th_title from "../../../components/Th_title";
import {resetContents, setApprovalProcesses} from "../../../redux/slice/contentSlice";
import {saveServiceRequest} from "../../../api/serviceRequest/ServiceRequestService";
import ApprovalComponent from "../../settings/approval/new/ApprovalComponent";
import {getApprovalProcessSubmitPayload, clearApprovalProcessState} from "../../settings/approval/shared/approvalProcessStore";
import {SLARadioAttributes} from "../../../constant/RadioGroupAttributes";
import SimpleRadioGroup from "../../../components/input/SimpleRadioGroup";
import {handleValidation} from "./serviceUtils";
import {useTranslation} from "react-i18next";
import SearchSelector from "../../../components/searchSelector/SearchSelector";
import UserSelectionField from "../../../components/selection/UserSelectionField";
import Selector from "../../../components/selector/Selector";
import {dateOnChange} from "../../../components/commonmethod/simmsDateMethod";
import SimmsDatePicker from "../../../components/datepicker/SimmsDatePicker";
import {useCacheRefresh} from "../../../hooks/useCacheRefresh";
import SimmsInputSearch from "../../../components/input/SimmsInputSearch";
import {onCreateIdClickListener} from "../../../components/commonmethod/simmsDrawerMethod";
import SimmsDrawerSearch from "../../../components/drawer/simms/SimmsDrawerSearch";
import dayjs from "dayjs";
import SimmsInput from "../../../components/input/SimmsInput";
import {inputOnChange} from "../../../components/commonmethod/simmsInputMethod";
import SimmsTextArea from "../../../components/input/SimmsTextArea";
import {
    ContentDetailThreeColLayout,
    DetailFilePanel,
    DetailKvReadOnly,
    DetailKvTable,
    DetailFormPageShell,
    DetailListPage,
    DetailPanel,
    DetailSummaryButton,
} from "../../../components/detailLayout";
import "../../../components/detailLayout/detailListLayout.css";

const ServiceRequestCreate = () => {
    const [serviceRequest, setServiceRequest] = useState({sla: true, serviceCodeId: 0});
    const [docs, setDocs] = useState([]);

    const user = useSelector(state => state.auth.user);
    const approvalProcessId = useSelector((state) => state.content.approvalProcess?.id);
    const currentDate = new Date();
    const dispatch = useDispatch();
    const refreshCache = useCacheRefresh();

    const {t} = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(resetContents());
        return () => dispatch(resetContents());
    }, [dispatch]);

    useEffect(() => {
        handleSetData();
    }, [serviceRequest.serviceCodeId]);

    /**
        서비스코드 선택시 서비스 코드에 저장된 처리담당자와 결재라인을 지정해준다.
     */
    const handleSetData = async () => {
        if (serviceRequest.serviceCodeId) {
            await getServiceCode(serviceRequest.serviceCodeId, (data) => {
                const handlers = data.handlers;
                if (Array.isArray(handlers) && handlers.length > 0) {
                    setServiceRequest((prev) => (
                        {...prev, handlers: [...data.handlers], handlerIds: [...data.handlers.map(handler => handler.id)]}
                    ));
                } else {
                    setServiceRequest((prev) => ({...prev, handlers: [], handlerIds: []}));
                }
                if (data.approvalProcess?.id) {
                    dispatch(setApprovalProcesses([data.approvalProcess]));
                } else {
                    clearApprovalProcessState(dispatch);
                }
            });
        } else {
            clearApprovalProcessState(dispatch);
        }
    }

    const handleHandlersChange = (handlers) => {
        setServiceRequest((prev) => ({
            ...prev,
            handlers,
            handlerIds: handlers.map((handler) => handler.id),
        }));
    };

    const handleHandlerRemove = (removed) => {
        setServiceRequest((prev) => ({
            ...prev,
            handlers: (prev.handlers ?? []).filter((handler) => handler.id !== removed.id),
            handlerIds: (prev.handlerIds ?? []).filter((handlerId) => handlerId !== removed.id),
        }));
    };

    const create = async () => {
        const data = {
            ...serviceRequest,
            ...getApprovalProcessSubmitPayload(),
        }
        if (!handleValidation(data.serviceCodeId, "요청코드는 필수값입니다.")) return;
        if (!handleValidation(data.applierId, "요청자는는 필수값입니다.")) return;
        if (!handleValidation(data.requesterId, "접수자는 필수값입니다.")) return;
        if (!handleValidation(data.handlerIds, "처리담당자는 필수값입니다.")) return;
        if (!handleValidation(data.requestCloseDate, "요청마감일은 필수값입니다.")) return;
        if (!handleValidation(data.title, "신청제목은 필수값입니다.")) return;

        await saveServiceRequest(data, docs, (id) => {
            window.alert(t(`saved`));
            refreshCache(CacheType.SERVICE_REQUEST);
            navigate(`/serviceRequest/serviceRequest/${id}`);
        });
    }

    const requestInfoPanel = (
        <DetailPanel title={"기본정보"}>
            <DetailKvTable>
                <tbody>
                <tr>
                    <Th_title title={"신청제목"} className="fieldset-td" colspan={3} required auth>
                        <SimmsInput
                            onChange={(v)=> inputOnChange(setServiceRequest,"title", v, true)}
                            data={serviceRequest.title}/>
                    </Th_title>
                </tr>
                <tr className="asset-detail-kv-row--auto">
                    <Th_title title={"신청내용"} className="fieldset-td" colspan={3} auth>
                        <SimmsTextArea
                            onChange={(v)=> inputOnChange(setServiceRequest,"description", v, true)}
                            data={serviceRequest.description}
                            style={{minHeight:"100px"}}/>
                    </Th_title>
                </tr>
                <tr>
                    <DetailKvReadOnly
                        label="등록일시"
                        value={currentDate.toISOString()}
                        type="date"
                    />
                    <DetailKvReadOnly label="수정일시" value=""/>
                </tr>
                <tr>
                    <DetailKvReadOnly label="등록자" value={user?.name}/>
                    <DetailKvReadOnly label="수정자" value=""/>
                </tr>
                <tr>
                    <Th_title title={"요청코드"} className="fieldset-td" colspan={3} required auth>
                        <Selector
                            onChange={(value)=> onCreateIdClickListener(
                                setServiceRequest, CodeType.serviceCode, CodeIdType.serviceCodeId,
                                serviceRequest.serviceCode, value
                            )}
                            data={serviceRequest.serviceCode}
                            entity={"serviceCode"}
                            uniqueKey={CodeType.serviceCode}
                            recursive auth
                        />
                    </Th_title>
                </tr>
                <tr>
                    <Th_title title={"요청자"} colspan={3} className="fieldset-td fieldset-td-input" required auth>
                        <Selector
                            onChange={(value)=> onCreateIdClickListener(
                                setServiceRequest, UserType.applier, UserIdType.applierId, serviceRequest.applier, value
                            )}
                            data={serviceRequest.applier}
                            uniqueKey={UserType.applier}
                            entity={"userAccount"} auth>
                            <SimmsInputSearch type={'TEXT'} placeHolder={"사용자명"} />
                            <SimmsDrawerSearch showLabel title={'부서'} entity={'department'} target={'department'} recursive />
                            <SimmsDrawerSearch showLabel title={'업체'} entity={'company'} target={'company'} />
                        </Selector>
                    </Th_title>
                </tr>
                <tr>
                    <Th_title title={"접수자"} colspan={3} className="fieldset-td fieldset-td-input" required auth>
                        <Selector
                            onChange={(v)=> onCreateIdClickListener(setServiceRequest, UserType.requester, UserIdType.requesterId,
                                serviceRequest.requester, v)}
                            data={serviceRequest.requester}
                            uniqueKey={UserType.requester}
                            entity={"userAccount"} auth>
                            <SimmsInputSearch type={'TEXT'} placeHolder={"사용자명"} />
                            <SimmsDrawerSearch showLabel title={'부서'} entity={'department'} target={'department'} recursive />
                            <SimmsDrawerSearch showLabel title={'업체'} entity={'company'} target={'company'} />
                        </Selector>
                    </Th_title>
                </tr>
                <tr>
                    <Th_title title={"요청마감일"} className="fieldset-td" colspan={3} required auth>
                        <div>
                        <SimmsDatePicker onChange={(v)=> dateOnChange(setServiceRequest,"requestCloseDate", v, true)}
                                         constraint={{largerThan : dayjs(new Date()), required: true}}
                                         data={serviceRequest.requestCloseDate} auth/>
                        </div>
                    </Th_title>
                </tr>
                <tr>
                    <Th_title title={"SLA 사용여부"} className="fieldset-td" colspan={3} required auth>
                        <SimpleRadioGroup
                            radioAttributes={SLARadioAttributes}
                            defaultValue={SLARadioAttributes[0].value}
                            data={{data: serviceRequest, setData: setServiceRequest}}
                            target={"sla"}
                        />
                    </Th_title>
                </tr>
                </tbody>
            </DetailKvTable>
        </DetailPanel>
    );

    const statusPanel = (
        <>
            <DetailPanel title={"처리담당자"}>
                <DetailKvTable>
                    <tbody>
                    <tr className="asset-detail-kv-row--auto">
                        <Th_title title={"처리담당자"} colspan={3} className="fieldset-td fieldset-td-input relative align-top" required auth>
                            <UserSelectionField
                                layout="inline"
                                data={serviceRequest.handlers ?? []}
                                onRemove={handleHandlerRemove}
                            >
                                <SearchSelector
                                    auth
                                    multiSelect
                                    variant="compact"
                                    entity="userAccount"
                                    uniqueKey={UserType.handlers}
                                    data={serviceRequest.handlers ?? []}
                                    placeHolder="사용자명"
                                    onChange={(value) => {
                                        if (value?.__multiFlat) {
                                            handleHandlersChange(value.__multiFlat);
                                        }
                                    }}
                                >
                                    <SimmsInputSearch type="TEXT" placeHolder="사용자명" />
                                    <SimmsDrawerSearch showLabel title="부서" entity="department" target="department" recursive />
                                    <SimmsDrawerSearch showLabel title="업체" entity="company" target="company" />
                                </SearchSelector>
                            </UserSelectionField>
                        </Th_title>
                    </tr>
                    </tbody>
                </DetailKvTable>
            </DetailPanel>
            <ApprovalComponent auth approvalProcessId={approvalProcessId}/>
        </>
    );

    const asidePanel = (
        <>
            {serviceRequest.serviceCodeId ? (
                <DetailFilePanel
                    title="서비스코드 파일"
                    isEditing={false}
                    tableInfo={{
                        tableName: ContentCategory.SERVICE_CODE,
                        joinKey: serviceRequest.serviceCodeId,
                    }}
                />
            ) : (
                <DetailPanel title={"서비스코드 파일"}>
                    <div className="text-[#b1bac3] text-[13px] p-3">
                        서비스코드를 선택하면 관련 파일이 표시됩니다.
                    </div>
                </DetailPanel>
            )}
            <DetailFilePanel
                pendingFiles={{files: docs, setFiles: setDocs}}
                pendingInputId="service_request_pending_doc"
            />
        </>
    );

    return (
        <DetailFormPageShell
            className="common-container container-height-full border-l-gray create-page detail-list-layout asset-detail-list-layout"
            title="접수/처리 등록"
            meta="신규 서비스 접수 정보를 입력하세요"
            actions={
                <>
                    <DetailSummaryButton muted onClick={() => navigate("/serviceRequest/serviceRequest")}>
                        취소
                    </DetailSummaryButton>
                    <DetailSummaryButton
                        onClick={() => {
                            if (window.confirm("등록 하시겠습니까?")) {
                                create();
                            }
                        }}
                    >
                        등록
                    </DetailSummaryButton>
                </>
            }
        >
            <DetailListPage isEditing className="flex min-h-0 flex-1 flex-col">
                <div className="container-height-body asset-detail-body detail-list-body flex min-h-0 flex-1 flex-col overflow-hidden">
                    <ContentDetailThreeColLayout
                        primary={requestInfoPanel}
                        secondary={statusPanel}
                        aside={asidePanel}
                    />
                </div>
            </DetailListPage>
        </DetailFormPageShell>
    );
};

export default ServiceRequestCreate;
